import { useState, useMemo } from "react";
import { MOCK_CARDS, MOCK_SESSIONS } from "../lib/mockData";
import SwipeCard from "../components/SwipeCard";
import FilterDrawer from "../components/FilterDrawer";
import EmptyState from "../components/EmptyState";
import LogoSwap from "../components/LogoSwap";
import LanguageToggle from "../components/LanguageToggle";
import MatchActionModal from "../components/MatchActionModal";
import MessageRequestForm from "../components/MessageRequestForm";
import { Search } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useT } from "../lib/useLanguage";
import { toast } from "sonner";

const QUICK_FILTERS = ["Tout", "Langues", "Études", "Digital", "CV / Emploi", "Sport", "Créatif", "Vie pratique"];

const SESSION_CARDS = MOCK_SESSIONS.slice(0, 12).map(s => ({
  id: "sc_" + s.id, type: "session", title: s.title, category: s.category,
  tags: [s.skill, s.level, s.format].filter(Boolean), userId: s.hostId, zone: s.zone,
  duration: s.duration, format: s.format, description: s.description,
}));

const ALL_CARDS = [...MOCK_CARDS, ...SESSION_CARDS];

const SUBTITLES_FR = ["Suggestions du moment", "Autour de toi", "Skills populaires", "Nouveaux swaps près de toi"];
const SUBTITLES_EN = ["Fresh suggestions", "Around you", "Popular skills", "New swaps near you"];

export default function Discover() {
  const t = useT();
  const lang = localStorage.getItem("swap_lang") || "fr";
  const [search, setSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState("Tout");
  const [filters, setFilters] = useState({ types: [], categories: [], zones: [], formats: [], durations: [] });
  const [dismissed, setDismissed] = useState([]);
  const [matchCard, setMatchCard] = useState(null);
  const [formCard, setFormCard] = useState(null);
  const subtitleIdx = useMemo(() => Math.floor(Math.random() * 4), []);
  const subtitle = lang === "en" ? SUBTITLES_EN[subtitleIdx] : SUBTITLES_FR[subtitleIdx];

  const filtered = useMemo(() => {
    let cards = ALL_CARDS.filter(c => !dismissed.includes(c.id));
    if (quickFilter !== "Tout") cards = cards.filter(c => c.category === quickFilter);
    if (filters.categories.length) cards = cards.filter(c => filters.categories.includes(c.category));
    if (filters.types.length) cards = cards.filter(c => filters.types.includes(c.type));
    if (filters.formats.length) cards = cards.filter(c => filters.formats.includes(c.format));
    if (search.trim()) {
      const q = search.toLowerCase();
      cards = cards.filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.tags?.some(tag => tag.toLowerCase().includes(q)) ||
        c.category.toLowerCase().includes(q)
      );
    }
    return cards;
  }, [search, quickFilter, filters, dismissed]);

  function handleAction(cardId, action) {
    const card = ALL_CARDS.find(c => c.id === cardId);
    if (action === "positive") {
      setMatchCard(card);
    } else if (action === "skip") {
      setDismissed(prev => [...prev, cardId]);
    } else if (action === "save") {
      setDismissed(prev => [...prev, cardId]);
      toast.success("Gardé pour plus tard ✓", { duration: 1500 });
    }
  }

  function handleWriteNow() {
    const card = matchCard;
    setMatchCard(null);
    setFormCard(card);
  }

  function handleSaveLater() {
    if (matchCard) {
      setDismissed(prev => [...prev, matchCard.id]);
      toast.success("Gardé pour plus tard ✓", { duration: 1500 });
    }
    setMatchCard(null);
  }

  function handleFormSent() {
    if (formCard) setDismissed(prev => [...prev, formCard.id]);
    setFormCard(null);
  }

  function resetAll() {
    setDismissed([]);
    setSearch("");
    setQuickFilter("Tout");
    setFilters({ types: [], categories: [], zones: [], formats: [], durations: [] });
  }

  const topCard = filtered[filtered.length - 1];
  const nextCard = filtered[filtered.length - 2];

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 flex flex-col" style={{ minHeight: "calc(100vh - 80px)" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <LogoSwap size="sm" />
        <LanguageToggle />
      </div>

      {/* Subtitle */}
      <p className="text-xs font-semibold text-muted-foreground mb-3">{subtitle}</p>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("discover_search")}
          className="w-full bg-white border border-border rounded-2xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {QUICK_FILTERS.map(f => (
            <button key={f}
              onClick={() => setQuickFilter(quickFilter === f ? "Tout" : f)}
              className={`shrink-0 px-3.5 py-2 rounded-full text-xs font-bold transition-all border ${quickFilter === f && f !== "Tout" ? "text-white border-transparent" : "bg-white border-border text-muted-foreground"}`}
              style={quickFilter === f && f !== "Tout" ? { backgroundColor: "#FF6B4A", borderColor: "#FF6B4A" } : {}}>
              {f}
            </button>
          ))}
        </div>
        <FilterDrawer filters={filters} onChange={setFilters} />
      </div>

      {/* Card stack */}
      <div className="relative flex-1" style={{ minHeight: 440, maxHeight: 480 }}>
        {nextCard && (
          <div className="absolute inset-0 z-0" style={{ transform: "scale(0.95) translateY(10px)", opacity: 0.35 }}>
            <div className="bg-white rounded-3xl border border-border h-full shadow" />
          </div>
        )}
        <AnimatePresence>
          {topCard && (
            <SwipeCard
              key={topCard.id}
              card={topCard}
              onAction={(action) => handleAction(topCard.id, action)}
              style={{ zIndex: 2 }}
            />
          )}
        </AnimatePresence>
        {!topCard && (
          <div className="h-full">
            <EmptyState onReset={resetAll} onCreateRequest={() => toast.info(t("error_login"))} />
          </div>
        )}
      </div>

      {/* Modals */}
      <MatchActionModal
        card={matchCard}
        onWriteNow={handleWriteNow}
        onSaveLater={handleSaveLater}
        onClose={() => setMatchCard(null)}
      />
      <MessageRequestForm
        card={formCard}
        onSent={handleFormSent}
        onClose={() => setFormCard(null)}
      />
    </div>
  );
}